import React, { useState } from 'react';
import { Check, Printer, ArrowRight, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import moment from 'moment-timezone';

const PagoList = ({ 
    pagos, 
    totalResults, 
    currentPage = 1,
    itemsPerPage = 100,
    onPageChange,
    selectedRows, toggleSelection, handlePrintClick, loading, renderEstado 
}) => {
    const totalCount = totalResults || pagos.length;
    // Pagination & Sort State (Client-Side Sorting solo para la página actual)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Sorting Logic
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedPagos = () => {
        if (!sortConfig.key) return pagos;

        return [...pagos].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'medidor') aValue = a.codigo_medidor || a.medidor?.codigo_medidor;
            if (sortConfig.key === 'medidor') bValue = b.codigo_medidor || b.medidor?.codigo_medidor;
            
            if (sortConfig.key === 'cliente') aValue = a.nombre_cliente || (a.medidor?.cliente ? `${a.medidor.cliente.nombres} ${a.medidor.cliente.apellidos}` : '');
            if (sortConfig.key === 'cliente') bValue = b.nombre_cliente || (b.medidor?.cliente ? `${b.medidor.cliente.nombres} ${b.medidor.cliente.apellidos}` : '');

            if (sortConfig.key === 'fecha' || sortConfig.key === 'mes') {
                aValue = new Date(a.fecha_lectura || a.createdAt).getTime();
                bValue = new Date(b.fecha_lectura || b.createdAt).getTime();
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // La data 'pagos' ya viene paginada desde el servidor en bloques de N (ej: 100).
    const currentPagos = getSortedPagos();

    // Valores calculados para el Footer
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = indexOfFirstItem + currentPagos.length;

    const paginate = (pageNumber) => {
        if (onPageChange) onPageChange(pageNumber);
    };

    // Header Helper
    const SortableHeader = ({ label, sortKey, className = "" }) => (
        <th 
            className={`px-2 py-2 text-left cursor-pointer group hover:bg-gray-100 transition-colors ${className}`}
            onClick={() => handleSort(sortKey)}
        >
            <div className="flex items-center gap-1">
                {label}
                <span className="text-gray-400 group-hover:text-blue-600">
                    {sortConfig.key === sortKey ? (
                        sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                    ) : (
                        <ArrowUpDown size={14} className="opacity-0 group-hover:opacity-50" />
                    )}
                </span>
            </div>
        </th>
    );

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full">
            <div className="overflow-x-auto">
                <table className="w-full min-w-[1200px] text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold text-center whitespace-nowrap">
                            <SortableHeader label="Medidor" sortKey="medidor" className="w-[80px]" />
                            <SortableHeader label="Cliente" sortKey="cliente" />
                            <th className="px-2 py-2">Lect. Ant</th>
                            <th className="px-2 py-2">Lect. Act</th>
                            <SortableHeader label="Mes" sortKey="mes" className="text-center" />
                            <th className="px-2 py-2">Consumo</th>
                            <th className="px-2 py-2">Base ($)</th>
                            <th className="px-2 py-2">Exceso ($)</th>
                            <th className="px-2 py-2">Mora</th>
                            <th className="px-2 py-2">Reconexión</th>
                            <SortableHeader label="Fecha" sortKey="fecha" />
                            <th className="px-2 py-2">Sub Agua</th>
                            <th className="px-2 py-2">Deudas</th>
                            <th className="px-2 py-2">Total</th>
                            <SortableHeader label="Estado" sortKey="estado" />
                            <th className="px-2 py-2">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            <tr>
                                <td colSpan="13" className="px-6 py-8 text-center text-gray-500">
                                    <div className="flex justify-center items-center">
                                        <span className="animate-spin mr-2">⏳</span>
                                        Cargando datos...
                                    </div>
                                </td>
                            </tr>
                        ) : pagos.length === 0 ? (
                            <tr>
                                <td colSpan="13" className="px-6 py-8 text-center text-gray-500">
                                    No se encontraron registros.
                                </td>
                            </tr>
                        ) : (
                            currentPagos.map((pago, idx) => {
                                // Important: We need the global index for selection to match the original array
                                const originalIndex = pagos.findIndex(p => p === pago);

                                return (
                                    <tr 
                                        key={idx} 
                                        onClick={() => (pago.estado !== 'PAGADO' && pago.estado_pago !== 'Pagado') && toggleSelection(originalIndex)}
                                        className={`transition-colors text-sm ${
                                            (pago.estado !== 'PAGADO' && pago.estado_pago !== 'Pagado') ? 'cursor-pointer hover:bg-blue-50/60' : 'hover:bg-gray-50'
                                        } ${selectedRows[originalIndex] ? 'bg-blue-50/80 font-medium' : ''}`}
                                    >
                                        <td className="px-2 py-2 font-medium text-gray-900">
                                            {pago.tipo === 'EXTRA' ? (
                                                <div className="flex items-center gap-1">
                                                    {pago.codigo_medidor && pago.codigo_medidor !== '---' && (
                                                        <span className="text-gray-600 font-medium">{pago.codigo_medidor}</span>
                                                    )}
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 uppercase">
                                                        Extra
                                                    </span>
                                                </div>
                                            ) : (
                                                pago.codigo_medidor || pago.medidor?.codigo_medidor
                                            )}
                                        </td>
                                        <td className="px-2 py-2 font-medium text-gray-700">
                                            {pago.tipo === 'EXTRA' ? (
                                                <div>
                                                    <div className="font-bold text-gray-900">
                                                        {pago.nombre_cliente || (pago.medidor?.cliente ? `${pago.medidor.cliente.nombres} ${pago.medidor.cliente.apellidos}` : '---')}
                                                    </div>
                                                    <div className="text-xs text-indigo-600 font-medium">
                                                        {pago.descripcion}
                                                        {pago.valor_total_original ? ` (Total: $${pago.valor_total_original.toFixed(2)} | Abonado: $${(pago.valor_ya_pagado || 0).toFixed(2)})` : ''}
                                                    </div>
                                                </div>
                                            ) : (
                                                (pago.medidor?.cliente ? `${pago.medidor.cliente.nombres} ${pago.medidor.cliente.apellidos}` : pago.nombre_cliente) || '---'
                                            )}
                                        </td>
                                        <td className="px-2 py-2 text-center text-gray-600">
                                            {pago.tipo === 'EXTRA' ? '---' : (pago.lecturas?.anterior || pago.lectura_anterior || 0)}
                                        </td>
                                        <td className="px-2 py-2 text-center text-gray-600">
                                            {pago.tipo === 'EXTRA' ? '---' : (pago.lecturas?.actual || pago.lectura_actual || 0)}
                                        </td>
                                        <td className="px-2 py-2 text-center uppercase text-gray-600 whitespace-nowrap">
                                            {pago.mes ? pago.mes.toUpperCase() : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-center">
                                            {pago.tipo === 'EXTRA' ? (
                                                <span className="text-gray-400">---</span>
                                            ) : (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {pago.consumos?.m3 || pago.consumo} m³
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-2 py-2 text-center text-gray-600">
                                            {pago.tipo === 'EXTRA' ? '---' : (Number(pago.valores?.base || 0) > 0 ? `$${pago.valores?.base}` : '0.00')}
                                        </td>
                                        <td className="px-2 py-2 text-center text-gray-600">
                                            {pago.tipo === 'EXTRA' ? '---' : (Number(pago.valores?.valorExceso || 0) > 0 ? `$${pago.valores?.valorExceso}` : '0.00')}
                                        </td>
                                        <td className="px-2 py-2 text-center text-red-600 font-medium">
                                            {pago.tipo === 'EXTRA' ? '---' : (Number(pago.valores?.mora || pago.mora || 0) > 0 ? `$${pago.valores?.mora || pago.mora}` : '0.00')}
                                        </td>
                                        <td className="px-2 py-2 text-center text-gray-600">
                                            {pago.tipo === 'EXTRA' ? '---' : (Number(pago.valores?.reconexion || 0) > 0 ? `$${pago.valores?.reconexion}` : '0.00')}
                                        </td>
                                        <td className="px-2 py-2 text-center text-gray-600 text-xs text-nowrap">
                                            {(pago.estado === 'PAGADO' || pago.estado_pago === 'Pagado') 
                                                ? (pago.fecha_pago ? moment(pago.fecha_pago).format('DD/MM/YYYY HH:mm:ss') : '-')
                                                : moment(pago.fecha_lectura || pago.fecha_emision).format('DD/MM/YYYY')
                                            }
                                        </td>
                                        <td className="px-2 py-2 text-center font-bold text-gray-800">
                                            ${(pago.valores?.subtotal || 0).toFixed(2)}
                                        </td>
                                        <td className="px-2 py-2 text-center font-bold text-gray-700">
                                            ${(pago.valores?.deuda_extra || 0).toFixed(2)}
                                        </td>
                                        <td className="px-2 py-2 text-center font-bold text-blue-700 bg-blue-50/50">
                                            ${(pago.valores?.total || 0).toFixed(2)}
                                        </td>
                                    <td className="px-2 py-2 text-center">
                                        {renderEstado(pago.estado || pago.estado_pago)}
                                    </td>
                                    <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                        {(pago.estado !== 'PAGADO' && pago.estado_pago !== 'Pagado') ? (
                                            <button 
                                                onClick={() => toggleSelection(originalIndex)}
                                                className={`p-1.5 rounded-full transition-all duration-200 transform hover:scale-105 ${
                                                    selectedRows[originalIndex] 
                                                        ? 'bg-green-500 text-white shadow-md' 
                                                        : 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
                                                }`}
                                                title={selectedRows[originalIndex] ? "Confirmado para pago" : "Añadir a pago"}
                                            >
                                                {selectedRows[originalIndex] ? <Check className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                                            </button>
                                        ) : (
                                            <button 
                                                onClick={() => handlePrintClick(pago)}
                                                className="p-1.5 rounded-full bg-amber-700 text-white hover:bg-amber-800 shadow-sm transition-colors"
                                                title="Imprimir Comprobante"
                                            >
                                                <Printer className="w-4 h-4" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            {!loading && totalCount > 0 && (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                    <div className="text-sm text-gray-500">
                        Mostrando {indexOfFirstItem + 1} - {indexOfLastItem} de {totalCount} resultados
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4 text-gray-600" />
                        </button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => Math.abs(page - currentPage) <= 2 || page === 1 || page === totalPages)
                            .map((page, index, array) => {
                                const addEllipsis = index > 0 && page - array[index - 1] > 1;
                                return (
                                    <React.Fragment key={page}>
                                        {addEllipsis && <span className="text-gray-400 px-1">...</span>}
                                        <button
                                            onClick={() => paginate(page)}
                                            className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                                                currentPage === page
                                                    ? 'bg-blue-600 text-white shadow-sm'
                                                    : 'text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    </React.Fragment>
                                );
                            })
                        }

                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PagoList;
